import { useEffect, useState } from 'react';

/**
 * Whether the focused element is one that summons the on-screen keyboard —
 * text-like inputs, textareas, and contenteditable surfaces (the chat
 * composer). Button-like inputs never raise a keyboard.
 */
function summonsOnScreenKeyboard(el: Element | null): boolean {
  if (!(el instanceof HTMLElement)) return false;
  if (el.isContentEditable) return true;
  if (el instanceof HTMLTextAreaElement) return true;
  if (el instanceof HTMLInputElement) {
    return !['button', 'checkbox', 'radio', 'range', 'file', 'submit', 'reset', 'color'].includes(
      el.type,
    );
  }
  return false;
}

/**
 * Returns whether the on-screen keyboard is (very likely) open: a
 * keyboard-summoning element is focused on a coarse-pointer (touch) device.
 *
 * Focus is used as the signal instead of viewport-height heuristics because
 * the two keyboard models disagree: iOS overlays the layout viewport
 * (`window.innerHeight` unchanged) while Android with
 * `interactive-widget=resizes-content` shrinks it — there is no single
 * viewport delta that detects both. Devices with a fine pointer (hardware
 * keyboard likely) always report `false`.
 */
export function useOnScreenKeyboardOpen(): boolean {
  const [open, setOpen] = useState(false);

  useEffect(() => {
    if (typeof window === 'undefined') return;
    if (!window.matchMedia('(pointer: coarse)').matches) return;

    let raf = 0;
    // focusout fires before the next element gains focus, so read
    // `document.activeElement` a frame later — moving focus between two
    // fields must not flash the closed state in between.
    const update = () => {
      cancelAnimationFrame(raf);
      raf = requestAnimationFrame(() => setOpen(summonsOnScreenKeyboard(document.activeElement)));
    };
    update();
    document.addEventListener('focusin', update);
    document.addEventListener('focusout', update);
    return () => {
      cancelAnimationFrame(raf);
      document.removeEventListener('focusin', update);
      document.removeEventListener('focusout', update);
    };
  }, []);

  return open;
}
